import React, { useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { FiLayers, FiPlus } from "react-icons/fi";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { ErrorResponse } from "@core/types/errorResponse";
import InfoTooltip from "@core/components/InfoTooltip";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { useReportSubGroupActions } from "@features/report/subgroup/hooks/useReportSubGroupActions";
import { CreateReportSubGroupRequest } from "@features/report/subgroup/types/reportSubGroupRequests";

type FieldErrors = Partial<Record<keyof CreateReportSubGroupRequest, string[]>>;

const CreateSubGroupModal: React.FC<{
    show: boolean;
    onHide: () => void;
    nationId: number;
    onCreated?: () => void;
}> = ({ show, onHide, nationId, onCreated }) => {
    const scopeId = useScopeId();
    const resolvedNationId = nationId ?? scopeId ?? 0;

    const [form, setForm] = useState<CreateReportSubGroupRequest>({
        name: "",
        description: "",
    });
    const [formErrors, setFormErrors] = useState<FieldErrors>({});
    const showError = useApiError();
    const showValidation = useErrorModalStore((state) => state.showValidation);
    const { busy, doSubGroupCreate } = useReportSubGroupActions();

    const handleChange = (field: keyof CreateReportSubGroupRequest) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        if (formErrors[field]) {
            setFormErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const resetForm = () => {
        setForm({
            name: "",
            description: "",
        });
        setFormErrors({});
    };

    const handleSubmit = async () => {
        const errors: FieldErrors = {};
        const messages: string[] = [];

        if (!form.name?.trim()) {
            errors.name = ["Name is required."];
            messages.push("Name is required.");
        }

        if (messages.length > 0) {
            setFormErrors(errors);
            showValidation(messages);
            return;
        }

        try {
            const response = await doSubGroupCreate(resolvedNationId, form);
            if (response.isSuccess()) {
                showSuccess(response.getData()?.message ?? "Report sub group created successfully.");
                resetForm();
                onHide();
                onCreated?.();
                return;
            }

            const error = response.getError() as ErrorResponse;
            const fieldErrors = (error?.errors as FieldErrors) ?? {};
            setFormErrors(fieldErrors);
            const list = Object.values(fieldErrors).flat().filter(Boolean);
            showValidation(list.length ? list : [error?.detail || "Failed to create report sub group."]);
            showError(error);
        } catch (error) {
            showError(error as ErrorResponse);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
            data-testid="create-sub-group-modal"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-semibold">
                    <FiLayers className="me-2" aria-hidden />
                    Create Report Sub Group
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3">
                <Form>
                    <Form.Group className="mb-3" controlId="subGroupName">
                        <Form.Label>
                            Name
                            <span className="text-danger">*</span>
                            <InfoTooltip text="Name shown when choosing or assigning a subgroup, such as Week 1, Adults, or Follow Up." />
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Sub group name"
                            value={form.name}
                            onChange={handleChange("name")}
                            isInvalid={!!formErrors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formErrors.name?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="subGroupDescription">
                        <Form.Label>
                            Description
                            <InfoTooltip text="Optional notes to help admins understand what this subgroup represents." />
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Sub group description"
                            value={form.description ?? ""}
                            onChange={handleChange("description")}
                            isInvalid={!!formErrors.description}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formErrors.description?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <div className="d-flex justify-content-end mt-3">
                        <Button
                            variant="primary"
                            size="lg"
                            className="btn-action"
                            onClick={handleSubmit}
                            disabled={busy}
                            aria-busy={busy}
                        >
                            {busy ? (
                                <>
                                    <Spinner size="sm" animation="border" className="me-2" />
                                    Creating Sub Group...
                                </>
                            ) : (
                                <>
                                    <FiPlus className="me-2" />
                                    Create Sub Group
                                </>
                            )}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default CreateSubGroupModal;
